import type {AxiosResponse} from "axios";

import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {briefingBoardsApi} from "../api";
import type {
  BriefingBoard,
  BriefingBoardAcknowledgment,
  BriefingBoardCreatePayload,
  BriefingBoardPendingView,
  BriefingBoardRecipient,
  BriefingBoardRecipientApi,
  BriefingBoardUpdatePayload,
} from "../types";

import {
  serializeBriefingBoardCreatePayload,
  serializeBriefingBoardUpdatePayload,
} from "./serializer";

export const BRIEFING_BOARD_INBOX_PAGE_SIZE = 15;
export const BRIEFING_BOARD_RECIPIENTS_PAGE_SIZE = 50;
/** Smaller pages when recipients render inside the briefing detail modal */
export const BRIEFING_BOARD_EMBEDDED_RECIPIENTS_PAGE_SIZE = 10;

function unwrapData<T>(response: AxiosResponse<{data: T}>): T {
  return response.data.data;
}

function unwrapListResponse<T>(response: AxiosResponse): GetListResponse<T> {
  const body = response.data as {
    data?: T[] | {data?: T[]};
    meta?: {total: number; from?: number; to?: number};
  };

  const items = Array.isArray(body.data)
    ? body.data
    : Array.isArray((body.data as {data?: T[]})?.data)
      ? (body.data as {data: T[]}).data
      : [];

  const meta = body.meta ?? {total: 0};

  return {
    data: items,
    meta: {
      total: meta.total,
      from: meta.from ?? 0,
      to: meta.to ?? 0,
    },
  };
}

function normalizeRecipient(row: BriefingBoardRecipientApi): BriefingBoardRecipient {
  const waivedAt = row.waived_at ?? null;
  return {
    acknowledgment_id: row.acknowledgment_id,
    shift_id: row.shift_id,
    user_id: row.user_id,
    name: row.user?.name?.trim() ?? "",
    notified_at: row.notified_at,
    read_at: row.read_at,
    is_read: row.is_read ?? Boolean(row.read_at ?? waivedAt),
    waived_at: waivedAt,
    waived_reason: row.waived_reason ?? null,
    is_required: row.is_required,
  };
}

class BriefingBoardsService {
  async getBriefingBoards(
    options: ParamsOptions,
  ): Promise<GetListResponse<BriefingBoard>> {
    const response = await briefingBoardsApi.searchManagement(options);
    return unwrapListResponse<BriefingBoard>(response);
  }

  async getInbox(params?: {
    unread?: boolean;
    q?: string;
    page?: {limit: number; offset: number};
  }): Promise<GetListResponse<BriefingBoardAcknowledgment>> {
    const response = await briefingBoardsApi.getInbox({
      unread: params?.unread,
      q: params?.q,
      page: params?.page ?? {
        limit: BRIEFING_BOARD_INBOX_PAGE_SIZE,
        offset: 0,
      },
    });
    return unwrapListResponse<BriefingBoardAcknowledgment>(response);
  }

  async getPending(params?: {
    shift_id?: number;
    view?: BriefingBoardPendingView;
    page?: {limit: number; offset: number};
  }): Promise<GetListResponse<BriefingBoardAcknowledgment>> {
    const response = await briefingBoardsApi.getPending({
      shift_id: params?.shift_id,
      view: params?.view,
      page: params?.page ?? {
        limit: BRIEFING_BOARD_INBOX_PAGE_SIZE,
        offset: 0,
      },
    });
    return unwrapListResponse<BriefingBoardAcknowledgment>(response);
  }

  async getBriefingBoard(id: number): Promise<BriefingBoard> {
    const response = await briefingBoardsApi.getById(id);
    return unwrapData<BriefingBoard>(response as AxiosResponse<{data: BriefingBoard}>);
  }

  async getRecipients(
    id: number,
    options: ParamsOptions,
  ): Promise<GetListResponse<BriefingBoardRecipient>> {
    const response = await briefingBoardsApi.getRecipients(id, options);
    const list = unwrapListResponse<BriefingBoardRecipientApi>(response);
    return {
      ...list,
      data: list.data.map(normalizeRecipient),
    };
  }

  async createBriefingBoard(
    payload: BriefingBoardCreatePayload | Record<string, unknown>,
  ): Promise<unknown> {
    const data =
      "target_all_properties" in payload
        ? payload
        : serializeBriefingBoardCreatePayload(
            payload as Parameters<typeof serializeBriefingBoardCreatePayload>[0],
          );
    const {data: responseData} = await briefingBoardsApi.create(
      data as Record<string, unknown>,
    );
    return responseData;
  }

  async updateBriefingBoard(
    id: number,
    payload: BriefingBoardUpdatePayload | Record<string, unknown>,
  ): Promise<unknown> {
    const data =
      "brief" in payload && !("briefing_board_id" in payload)
        ? payload
        : serializeBriefingBoardUpdatePayload(
            payload as Parameters<typeof serializeBriefingBoardUpdatePayload>[0],
          );
    const {data: responseData} = await briefingBoardsApi.update(
      id,
      data as Record<string, unknown>,
    );
    return responseData;
  }

  async deleteBriefingBoard(id: number): Promise<void> {
    await briefingBoardsApi.delete(id);
  }

  async markBriefingBoardRead(id: number, shiftId: number): Promise<void> {
    await briefingBoardsApi.markRead(id, {shift_id: shiftId});
  }
}

export const briefingBoardsService = new BriefingBoardsService();

export {
  serializeBriefingBoardCreatePayload,
  serializeBriefingBoardUpdatePayload,
} from "./serializer";
